import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AcademicYearResponse } from '../../admin/schools/models/school-academic-year.models';
import { SchoolLevelResponse } from '../../admin/schools/models/school-level.models';
import { SchoolAdminService } from '../../admin/schools/services/school-admin.service';
import { PageRequest, PageResponse, toPageParams } from '../../../shared/commons/models/page';
import { CreateUpdateEnrollmentRequest } from '../models/create-update-request-model';
import { EnrollmentDetails } from '../models/enrollment-details.models';
import { EnrollmentFilters } from '../models/enrollment-filters.models';
import { EnrollmentResponse } from '../models/enrollment-response.models';
import { EnrollmentSummary } from '../models/enrollment-summary.models';

@Injectable({ providedIn: 'root' })
export class StudentEnrollmentService {
  private readonly http = inject(HttpClient);
  private readonly schoolApi = inject(SchoolAdminService);

  private readonly schoolsUrl = `${environment.apiUrl}/v1/schools`;

  getSchoolLevels(schoolPublicId: string): Observable<SchoolLevelResponse[]> {
    return this.schoolApi.levels(schoolPublicId);
  }

  loadAcademicYears(schoolPublicId: string): Observable<AcademicYearResponse[]> {
    return this.schoolApi.academicYears(schoolPublicId);
  }

  create(schoolPublicId: string, request: CreateUpdateEnrollmentRequest): Observable<EnrollmentResponse> {
    return this.http.post<EnrollmentResponse>(`${this.schoolsUrl}/${schoolPublicId}/enrollments`, request);
  }

  update(
    schoolPublicId: string,
    enrollmentPublicId: string,
    request: CreateUpdateEnrollmentRequest,
  ): Observable<EnrollmentResponse> {
    return this.http.put<EnrollmentResponse>(
      `${this.schoolsUrl}/${schoolPublicId}/enrollments/${enrollmentPublicId}`,
      request,
    );
  }

  list(
    schoolPublicId: string,
    request: PageRequest,
    filters: EnrollmentFilters = {},
  ): Observable<PageResponse<EnrollmentSummary>> {
    let params = toPageParams(request);
    for (const [key, value] of Object.entries(filters)) {
      const trimmed = typeof value === 'string' ? value.trim() : '';
      if (trimmed) {
        params = params.set(key, trimmed);
      }
    }
    return this.http.get<PageResponse<EnrollmentSummary>>(`${this.schoolsUrl}/${schoolPublicId}/enrollments`, {
      params,
    });
  }

  getEnrollmentDetails(schoolPublicId: string, enrollmentPublicId: string): Observable<EnrollmentDetails> {
    return this.http.get<EnrollmentDetails>(`${this.schoolsUrl}/${schoolPublicId}/enrollments/${enrollmentPublicId}`);
  }
}
